import {
  ref,
  get,
  set,
  update,
  push,
  query,
  orderByChild,
  limitToFirst,
} from "firebase/database";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage } from "../config/firebase";
import {
  ManagedUser,
  BanUserParams,
  UserStats,
  UserGrowthDataPoint,
  TokenPackage,
  CreateTokenPackageParams,
  SubscriptionPackage,
  CreateSubscriptionPackageParams,
  RevenueEvent,
  RevenueStats,
  RevenueChartDataPoint,
  PromoCode,
  CreatePromoCodeParams,
  MediaKit,
  MediaExample,
  UploadMediaParams,
  UpdateMediaParams,
  AdminActionLog,
  TokenRedemption,
  RedemptionType,
  RedemptionStats,
  RedemptionFilters,
  Campaign,
  CampaignFormParams,
  LanguageEntry,
  EventTemplate,
  EventTemplateFormParams,
} from "../types";
import { amsterdamLocalToUtcMs } from "../utils/timezone";

// Get Firebase Functions instance
const functions = getFunctions();

// Helper to extract clean error message from Firebase function errors
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Firebase function errors have the format: "Firebase: Error (functions/error-code)."
    // or just the message from HttpsError
    const message = error.message;

    // Check if it's a Firebase Functions error with a code
    if ("code" in error && typeof (error as any).code === "string") {
      const code = (error as any).code;
      // Common error codes and friendly messages
      if (code === "functions/unauthenticated") {
        return "You must be logged in to perform this action.";
      }
      if (code === "functions/permission-denied") {
        return "You do not have permission to perform this action.";
      }
      if (code === "functions/invalid-argument") {
        return message || "Invalid input provided.";
      }
      if (code === "functions/not-found") {
        return message || "The requested resource was not found.";
      }
      if (code === "functions/internal") {
        return message || "An internal error occurred. Please try again.";
      }
    }

    return message;
  }

  return "An unexpected error occurred. Please try again.";
}

/**
 * Admin service for managing users, packages, revenue, promo codes, media,
 * and redemptions. Reads from Firebase RTDB and delegates writes to
 * Cloud Functions. Supports test-mode isolation via a toggle.
 */
class AdminService {
  private testMode: boolean = false;

  setTestMode(enabled: boolean): void {
    this.testMode = enabled;
  }

  private getTestMode(): boolean {
    return this.testMode;
  }

  // ============ ADMIN AUTH ============

  async checkIsAdmin(userId: string): Promise<boolean> {
    // New structure: admin/users/{uid} = true
    const adminRef = ref(db, `admin/users/${userId}`);
    const snapshot = await get(adminRef);

    if (snapshot.exists()) {
      const value = snapshot.val();
      // Database structure: /admin/users/{userId} = true | false | undefined
      return value === true;
    }
    return false;
  }

  // ============ USER MANAGEMENT ============

  // New structure: user_data/{uid}/ contains profile/, subscription/, wallet/
  private getUserDataPath(): string {
    return this.testMode ? "testMode/user_data" : "user_data";
  }

  // Helper to convert new user_data structure to ManagedUser
  private mapUserDataToManagedUser(userId: string, userData: any): ManagedUser {
    const profile = userData?.profile || {};
    const subscription = userData?.subscription || {};
    const wallet = userData?.wallet || {};

    return {
      id: userId,
      email: profile.email || "",
      displayName: profile.displayName || null,
      photoURL: profile.photoURL || null,
      isBanned: profile.isBanned || false,
      banReason: profile.banReason,
      bannedAt: profile.bannedAt,
      bannedBy: profile.bannedBy,
      banExpiresAt: profile.banExpiresAt,
      subscriptionStatus: subscription.status || "none",
      subscriptionTier: subscription.entitlementId,
      tokenBalance: wallet.balance || 0,
      lifetimeSpent: wallet.lifetimeTokensSpent || 0,
      createdAt: profile.createdAt || Date.now(),
      lastLoginAt: profile.lastLoginAt,
    };
  }

  async getUsers(
    limit: number = 50,
    startAfter?: string,
    searchTerm?: string
  ): Promise<{ users: ManagedUser[]; hasMore: boolean }> {
    const path = this.getUserDataPath();
    console.log("[AdminService] getUsers called", {
      path,
      limit,
      searchTerm,
      testMode: this.testMode,
    });

    try {
      const usersRef = ref(db, path);
      const snapshot = await get(usersRef);
      const users: ManagedUser[] = [];

      console.log(
        "[AdminService] getUsers snapshot exists:",
        snapshot.exists()
      );

      if (snapshot.exists()) {
        snapshot.forEach((child) => {
          const userData = child.val();
          const profile = userData?.profile || {};

          // Filter by search term if provided
          if (searchTerm) {
            const term = searchTerm.toLowerCase();
            if (
              !profile.email?.toLowerCase().includes(term) &&
              !profile.displayName?.toLowerCase().includes(term)
            ) {
              return;
            }
          }

          users.push(this.mapUserDataToManagedUser(child.key!, userData));
        });
      }

      // Sort by createdAt descending
      users.sort((a, b) => b.createdAt - a.createdAt);

      console.log("[AdminService] getUsers found", users.length, "users");

      const hasMore = users.length > limit;
      return {
        users: users.slice(0, limit),
        hasMore,
      };
    } catch (error) {
      console.error("[AdminService] getUsers error:", error);
      console.error("[AdminService] getUsers path was:", path);
      throw error;
    }
  }

  async getUserById(userId: string): Promise<ManagedUser | null> {
    const userRef = ref(db, `${this.getUserDataPath()}/${userId}`);
    const snapshot = await get(userRef);

    if (snapshot.exists()) {
      return this.mapUserDataToManagedUser(userId, snapshot.val());
    }
    return null;
  }

  async banUser(params: BanUserParams): Promise<void> {
    const { userId, reason, duration, bannedBy } = params;
    const banExpiresAt = duration ? Date.now() + duration : null;

    // Update ban status in profile
    await update(ref(db, `${this.getUserDataPath()}/${userId}/profile`), {
      isBanned: true,
      banReason: reason,
      bannedAt: Date.now(),
      bannedBy,
      banExpiresAt,
    });

    await this.logAdminAction({
      adminId: bannedBy,
      adminEmail: "",
      action: "ban_user",
      targetType: "user",
      targetId: userId,
      details: { reason, duration, banExpiresAt },
    });
  }

  async unbanUser(userId: string, adminId: string): Promise<void> {
    // Update ban status in profile
    await update(ref(db, `${this.getUserDataPath()}/${userId}/profile`), {
      isBanned: false,
      banReason: null,
      bannedAt: null,
      bannedBy: null,
      banExpiresAt: null,
    });

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "unban_user",
      targetType: "user",
      targetId: userId,
      details: {},
    });
  }

  async getUserStats(): Promise<UserStats> {
    const usersRef = ref(db, this.getUserDataPath());
    const snapshot = await get(usersRef);

    let totalUsers = 0;
    let newUsersThisWeek = 0;
    let newUsersThisMonth = 0;
    let bannedUsers = 0;
    let activeSubscribers = 0;

    const now = Date.now();
    const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;
    const oneMonthAgo = now - 30 * 24 * 60 * 60 * 1000;

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        const userData = child.val();
        const profile = userData?.profile || {};
        const subscription = userData?.subscription || {};

        totalUsers++;

        const createdAt = profile.createdAt || 0;
        if (createdAt >= oneWeekAgo) newUsersThisWeek++;
        if (createdAt >= oneMonthAgo) newUsersThisMonth++;
        if (profile.isBanned) bannedUsers++;
        if (subscription.status === "active") activeSubscribers++;
      });
    }

    return {
      totalUsers,
      newUsersThisWeek,
      newUsersThisMonth,
      bannedUsers,
      activeSubscribers,
    };
  }

  async getUserGrowthData(days: number = 30): Promise<UserGrowthDataPoint[]> {
    const usersRef = ref(db, this.getUserDataPath());
    const snapshot = await get(usersRef);

    const dataMap = new Map<string, number>();
    const now = new Date();

    // Initialize all days with 0
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];
      dataMap.set(dateStr, 0);
    }

    // Count users per day
    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        const userData = child.val();
        const profile = userData?.profile || {};
        const createdAt = profile.createdAt;

        if (createdAt) {
          const dateStr = new Date(createdAt).toISOString().split("T")[0];
          if (dataMap.has(dateStr)) {
            dataMap.set(dateStr, (dataMap.get(dateStr) || 0) + 1);
          }
        }
      });
    }

    // Convert to array with cumulative count
    const result: UserGrowthDataPoint[] = [];
    let cumulative = 0;

    // Get total users before the period
    if (snapshot.exists()) {
      const cutoffDate = new Date(now);
      cutoffDate.setDate(cutoffDate.getDate() - days);
      snapshot.forEach((child) => {
        const userData = child.val();
        const profile = userData?.profile || {};
        const createdAt = profile.createdAt;

        if (createdAt && createdAt < cutoffDate.getTime()) {
          cumulative++;
        }
      });
    }

    Array.from(dataMap.entries()).forEach(([date, count]) => {
      cumulative += count;
      result.push({ date, count, cumulative });
    });

    return result;
  }

  // ============ TOKEN PACKAGES ============

  async getTokenPackages(): Promise<TokenPackage[]> {
    // New structure: admin/token_packages
    const packagePath = this.testMode
      ? "testMode/admin/token_packages"
      : "admin/token_packages";
    const packagesRef = ref(db, packagePath);
    const snapshot = await get(packagesRef);
    const packages: TokenPackage[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        packages.push({ id: child.key!, ...child.val() } as TokenPackage);
      });
    }

    return packages.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async getActiveTokenPackages(): Promise<TokenPackage[]> {
    const packages = await this.getTokenPackages();
    return packages.filter((p) => p.isActive);
  }

  async createTokenPackage(
    params: CreateTokenPackageParams,
    adminId: string
  ): Promise<TokenPackage> {
    // Call Cloud Function to create Stripe Product/Price and save to Firebase
    const createTokenPackageFn = httpsCallable<
      {
        name: string;
        description: string;
        tokenAmount: number;
        priceUsd: number;
        priceGbp: number;
        badge?: string;
        sortOrder?: number;
        testMode: boolean;
      },
      { id: string; stripeProductId: string; stripePriceId: string }
    >(functions, "createTokenPackage");

    console.log("[AdminService] Calling createTokenPackage Cloud Function", {
      params,
      testMode: this.testMode,
    });

    try {
      const result = await createTokenPackageFn({
        name: params.name,
        description: params.description,
        tokenAmount: params.tokenAmount,
        priceUsd: params.priceUsd,
        priceGbp: params.priceGbp,
        badge: params.badge || undefined,
        sortOrder: params.sortOrder || 0,
        testMode: this.testMode,
      });

      console.log("[AdminService] createTokenPackage result:", result.data);

      // Fetch the created package from Firebase to return full data
      const packagePath = this.testMode
        ? `testMode/admin/token_packages/${result.data.id}`
        : `admin/token_packages/${result.data.id}`;

      // Persist per-language translations as a subdoc on the same record.
      // Cloud Function only knows the canonical English fields (name,
      // description, badge); translations live in `translations.{lang}.*`
      // and are read with a fallback chain on the client.
      if (params.translations) {
        await update(ref(db, packagePath), {
          translations: params.translations,
        });
      }

      const packageRef = ref(db, packagePath);
      const snapshot = await get(packageRef);

      if (!snapshot.exists()) {
        throw new Error("Package created but not found in database");
      }

      return { id: result.data.id, ...snapshot.val() } as TokenPackage;
    } catch (error) {
      console.error("[AdminService] createTokenPackage error:", error);
      throw new Error(getErrorMessage(error));
    }
  }

  async updateTokenPackage(
    id: string,
    updates: Partial<TokenPackage>,
    adminId: string
  ): Promise<void> {
    // Call Cloud Function to update Stripe Product and Firebase
    const updateTokenPackageFn = httpsCallable<
      {
        packageId: string;
        updates: Partial<{
          name: string;
          description: string;
          badge: string | null;
          sortOrder: number;
          isActive: boolean;
        }>;
        testMode: boolean;
      },
      { success: boolean }
    >(functions, "updateTokenPackage");

    // Filter out undefined values to avoid overwriting existing data with undefined
    const cleanUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) cleanUpdates.name = updates.name;
    if (updates.description !== undefined)
      cleanUpdates.description = updates.description;
    if (updates.badge !== undefined) cleanUpdates.badge = updates.badge;
    if (updates.sortOrder !== undefined)
      cleanUpdates.sortOrder = updates.sortOrder;
    if (updates.isActive !== undefined)
      cleanUpdates.isActive = updates.isActive;
    if (updates.appStoreProductId !== undefined)
      cleanUpdates.appStoreProductId = updates.appStoreProductId;
    if (updates.playStoreProductId !== undefined)
      cleanUpdates.playStoreProductId = updates.playStoreProductId;

    console.log("[AdminService] Calling updateTokenPackage Cloud Function", {
      id,
      cleanUpdates,
      testMode: this.testMode,
    });

    try {
      await updateTokenPackageFn({
        packageId: id,
        updates: cleanUpdates,
        testMode: this.testMode,
      });

      // Persist per-language translations as a subdoc on the same record.
      // Cloud Function whitelists fields and won't propagate `translations`
      // through, so we write it directly. Path is the same record
      // (atomic w/r/t the Firebase RTDB write that just happened).
      if (updates.translations !== undefined) {
        const packagePath = this.testMode
          ? `testMode/admin/token_packages/${id}`
          : `admin/token_packages/${id}`;
        await update(ref(db, packagePath), {
          translations: updates.translations,
          updatedAt: Date.now(),
        });
      }

      console.log("[AdminService] updateTokenPackage success");
    } catch (error) {
      console.error("[AdminService] updateTokenPackage error:", error);
      throw new Error(getErrorMessage(error));
    }
  }

  async deactivateTokenPackage(id: string, adminId: string): Promise<void> {
    // Use the update function with isActive: false
    await this.updateTokenPackage(id, { isActive: false }, adminId);
    console.log("[AdminService] deactivateTokenPackage success", { id });
  }

  // ============ SUBSCRIPTION PACKAGES ============

  async getSubscriptionPackages(): Promise<SubscriptionPackage[]> {
    // New structure: admin/subscription_packages
    const packagePath = this.testMode
      ? "testMode/admin/subscription_packages"
      : "admin/subscription_packages";
    const packagesRef = ref(db, packagePath);
    const snapshot = await get(packagesRef);
    const packages: SubscriptionPackage[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        packages.push({
          id: child.key!,
          ...child.val(),
        } as SubscriptionPackage);
      });
    }

    return packages.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async getActiveSubscriptionPackages(): Promise<SubscriptionPackage[]> {
    const packages = await this.getSubscriptionPackages();
    return packages.filter((p) => p.isActive);
  }

  async createSubscriptionPackage(
    params: CreateSubscriptionPackageParams,
    adminId: string
  ): Promise<SubscriptionPackage> {
    // Call Cloud Function to create Stripe Product/Prices and save to Firebase
    const createSubscriptionPackageFn = httpsCallable<
      {
        name: string;
        description: string;
        tier: number;
        features: string[];
        tokensPerMonth: number;
        priceMonthlyUsd: number;
        priceMonthlyGbp: number;
        priceYearlyUsd: number;
        priceYearlyGbp: number;
        entitlementId: string;
        badge?: string;
        color: string;
        sortOrder?: number;
        // App Store / Play Store product IDs
        appStoreProductIdMonthly?: string;
        appStoreProductIdYearly?: string;
        playStoreProductIdMonthly?: string;
        playStoreProductIdYearly?: string;
        testMode: boolean;
      },
      {
        id: string;
        stripeProductId: string;
        stripePriceIdMonthly: string;
        stripePriceIdYearly: string;
      }
    >(functions, "createSubscriptionPackage");

    console.log(
      "[AdminService] Calling createSubscriptionPackage Cloud Function",
      { params, testMode: this.testMode }
    );

    try {
      const result = await createSubscriptionPackageFn({
        name: params.name,
        description: params.description,
        tier: params.tier || 1,
        features: params.features,
        tokensPerMonth: params.tokensPerMonth,
        priceMonthlyUsd: params.priceMonthlyUsd,
        priceMonthlyGbp: params.priceMonthlyGbp,
        priceYearlyUsd: params.priceYearlyUsd,
        priceYearlyGbp: params.priceYearlyGbp,
        entitlementId: params.entitlementId,
        badge: params.badge || undefined,
        color: params.color,
        sortOrder: params.sortOrder || 0,
        // App Store / Play Store product IDs
        appStoreProductIdMonthly: params.appStoreProductIdMonthly || undefined,
        appStoreProductIdYearly: params.appStoreProductIdYearly || undefined,
        playStoreProductIdMonthly:
          params.playStoreProductIdMonthly || undefined,
        playStoreProductIdYearly: params.playStoreProductIdYearly || undefined,
        testMode: this.testMode,
      });

      console.log(
        "[AdminService] createSubscriptionPackage result:",
        result.data
      );

      // Fetch the created package from Firebase to return full data
      const packagePath = this.testMode
        ? `testMode/admin/subscription_packages/${result.data.id}`
        : `admin/subscription_packages/${result.data.id}`;

      // Persist per-language translations as a subdoc. The Cloud Function
      // owns canonical English copy + features[]; translations are layered
      // on with a fallback chain on the client.
      if (params.translations) {
        await update(ref(db, packagePath), {
          translations: params.translations,
        });
      }

      const packageRef = ref(db, packagePath);
      const snapshot = await get(packageRef);

      if (!snapshot.exists()) {
        throw new Error("Package created but not found in database");
      }

      return { id: result.data.id, ...snapshot.val() } as SubscriptionPackage;
    } catch (error) {
      console.error("[AdminService] createSubscriptionPackage error:", error);
      throw new Error(getErrorMessage(error));
    }
  }

  async updateSubscriptionPackage(
    id: string,
    updates: Partial<SubscriptionPackage>,
    adminId: string
  ): Promise<void> {
    // Call Cloud Function to update Stripe Product and Firebase
    const updateSubscriptionPackageFn = httpsCallable<
      {
        packageId: string;
        updates: Partial<{
          name: string;
          description: string;
          features: string[];
          tokensPerMonth: number;
          badge: string | null;
          color: string;
          sortOrder: number;
          isActive: boolean;
          // App Store / Play Store product IDs
          appStoreProductIdMonthly: string;
          appStoreProductIdYearly: string;
          playStoreProductIdMonthly: string;
          playStoreProductIdYearly: string;
        }>;
        testMode: boolean;
      },
      { success: boolean }
    >(functions, "updateSubscriptionPackage");

    // Filter out undefined values to avoid overwriting existing data with undefined
    const cleanUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) cleanUpdates.name = updates.name;
    if (updates.description !== undefined)
      cleanUpdates.description = updates.description;
    if (updates.features !== undefined)
      cleanUpdates.features = updates.features;
    if (updates.tokensPerMonth !== undefined)
      cleanUpdates.tokensPerMonth = updates.tokensPerMonth;
    if (updates.badge !== undefined) cleanUpdates.badge = updates.badge;
    if (updates.color !== undefined) cleanUpdates.color = updates.color;
    if (updates.sortOrder !== undefined)
      cleanUpdates.sortOrder = updates.sortOrder;
    if (updates.isActive !== undefined)
      cleanUpdates.isActive = updates.isActive;
    // App Store / Play Store product IDs
    if (updates.appStoreProductIdMonthly !== undefined)
      cleanUpdates.appStoreProductIdMonthly = updates.appStoreProductIdMonthly;
    if (updates.appStoreProductIdYearly !== undefined)
      cleanUpdates.appStoreProductIdYearly = updates.appStoreProductIdYearly;
    if (updates.playStoreProductIdMonthly !== undefined)
      cleanUpdates.playStoreProductIdMonthly =
        updates.playStoreProductIdMonthly;
    if (updates.playStoreProductIdYearly !== undefined)
      cleanUpdates.playStoreProductIdYearly = updates.playStoreProductIdYearly;

    console.log(
      "[AdminService] Calling updateSubscriptionPackage Cloud Function",
      { id, cleanUpdates, testMode: this.testMode }
    );

    try {
      await updateSubscriptionPackageFn({
        packageId: id,
        updates: cleanUpdates,
        testMode: this.testMode,
      });

      // Persist translations subdoc directly (Cloud Function whitelists fields).
      if (updates.translations !== undefined) {
        const packagePath = this.testMode
          ? `testMode/admin/subscription_packages/${id}`
          : `admin/subscription_packages/${id}`;
        await update(ref(db, packagePath), {
          translations: updates.translations,
          updatedAt: Date.now(),
        });
      }

      console.log("[AdminService] updateSubscriptionPackage success");
    } catch (error) {
      console.error("[AdminService] updateSubscriptionPackage error:", error);
      throw new Error(getErrorMessage(error));
    }
  }

  async deactivateSubscriptionPackage(
    id: string,
    adminId: string
  ): Promise<void> {
    // Use the update function with isActive: false
    await this.updateSubscriptionPackage(id, { isActive: false }, adminId);
    console.log("[AdminService] deactivateSubscriptionPackage success", { id });
  }

  // ============ REVENUE ============

  async getRevenueEvents(
    limit: number = 50,
    filters?: { type?: string; startDate?: number; endDate?: number }
  ): Promise<RevenueEvent[]> {
    const basePath = this.testMode ? "testMode" : "";
    const eventsRef = ref(
      db,
      `${basePath ? basePath + "/" : ""}revenue_events`
    );
    const eventsQuery = query(
      eventsRef,
      orderByChild("createdAt"),
      limitToFirst(limit)
    );
    const snapshot = await get(eventsQuery);
    const events: RevenueEvent[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        const event = { id: child.key!, ...child.val() } as RevenueEvent;

        // Apply filters
        if (filters?.type && event.type !== filters.type) return;
        if (filters?.startDate && event.createdAt < filters.startDate) return;
        if (filters?.endDate && event.createdAt > filters.endDate) return;

        events.push(event);
      });
    }

    return events.sort((a, b) => b.createdAt - a.createdAt);
  }

  // Get all user transactions (for admin view)
  // Now reads from user_data/{uid}/transactions/ path (new structure)
  async getAllUserTransactions(
    limit: number = 100,
    filters?: {
      type?: string;
      startDate?: number;
      endDate?: number;
      search?: string;
    }
  ): Promise<
    Array<{
      id: string;
      userId: string;
      userEmail?: string;
      type: string;
      actionType?: string;
      amount: number;
      description?: string;
      status: string;
      createdAt: number;
      packageId?: string;
      pricePaid?: number;
      currency?: string;
      balanceBefore?: number;
      balanceAfter?: number;
    }>
  > {
    const basePath = this.testMode ? "testMode" : "";
    // New structure: user_data/{uid}/transactions/
    const userDataRef = ref(db, `${basePath ? basePath + "/" : ""}user_data`);
    const snapshot = await get(userDataRef);

    const allTransactions: Array<{
      id: string;
      userId: string;
      userEmail?: string;
      type: string;
      actionType?: string;
      amount: number;
      description?: string;
      status: string;
      createdAt: number;
      packageId?: string;
      pricePaid?: number;
      currency?: string;
      balanceBefore?: number;
      balanceAfter?: number;
    }> = [];

    if (snapshot.exists()) {
      // Iterate through each user's data
      snapshot.forEach((userNode) => {
        const userId = userNode.key!;
        const userData = userNode.val();
        const transactions = userData?.transactions;
        const profile = userData?.profile;
        const userEmail = profile?.email;

        if (transactions) {
          Object.entries(transactions).forEach(
            ([transactionId, transaction]: [string, any]) => {
              const txn = {
                id: transactionId,
                userId,
                userEmail,
                ...transaction,
              };

              // Apply filters
              if (filters?.type && txn.type !== filters.type) return;
              if (filters?.startDate && txn.createdAt < filters.startDate)
                return;
              if (filters?.endDate && txn.createdAt > filters.endDate) return;
              if (filters?.search) {
                const searchLower = filters.search.toLowerCase();
                if (
                  !txn.description?.toLowerCase().includes(searchLower) &&
                  !txn.userId?.toLowerCase().includes(searchLower) &&
                  !txn.type?.toLowerCase().includes(searchLower) &&
                  !txn.actionType?.toLowerCase().includes(searchLower) &&
                  !txn.userEmail?.toLowerCase().includes(searchLower)
                ) {
                  return;
                }
              }

              allTransactions.push(txn);
            }
          );
        }
      });
    }

    // Sort by createdAt descending and limit
    return allTransactions
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, limit);
  }

  // Get token spending statistics from new transaction system
  async getTokenSpendingStats(): Promise<{
    totalSpent: number;
    spendingToday: number;
    spendingThisWeek: number;
    spendingThisMonth: number;
    byActionType: Record<string, { count: number; tokens: number }>;
    averageTokensPerSpend: number;
  }> {
    const transactions = await this.getAllUserTransactions(10000, {
      type: "spend",
    });

    const now = Date.now();
    const todayStart = new Date().setHours(0, 0, 0, 0);
    const weekStart = now - 7 * 24 * 60 * 60 * 1000;
    const monthStart = now - 30 * 24 * 60 * 60 * 1000;

    const stats = {
      totalSpent: 0,
      spendingToday: 0,
      spendingThisWeek: 0,
      spendingThisMonth: 0,
      byActionType: {} as Record<string, { count: number; tokens: number }>,
      averageTokensPerSpend: 0,
    };

    let spendCount = 0;

    for (const tx of transactions) {
      const tokensSpent = Math.abs(tx.amount); // amount is negative for spends
      stats.totalSpent += tokensSpent;
      spendCount++;

      if (tx.createdAt >= todayStart) stats.spendingToday += tokensSpent;
      if (tx.createdAt >= weekStart) stats.spendingThisWeek += tokensSpent;
      if (tx.createdAt >= monthStart) stats.spendingThisMonth += tokensSpent;

      // Group by action type
      const actionType = tx.actionType || "unknown";
      if (!stats.byActionType[actionType]) {
        stats.byActionType[actionType] = { count: 0, tokens: 0 };
      }
      stats.byActionType[actionType].count++;
      stats.byActionType[actionType].tokens += tokensSpent;
    }

    stats.averageTokensPerSpend =
      spendCount > 0 ? Math.round(stats.totalSpent / spendCount) : 0;

    return stats;
  }

  async getRevenueStats(): Promise<RevenueStats> {
    // Fetch all transactions from /transactions/{uid} structure
    const transactions = await this.getAllUserTransactions(10000);

    let totalRevenue = 0;
    let revenueThisMonth = 0;
    let revenueLastMonth = 0;
    let tokenPurchaseRevenue = 0;
    let subscriptionRevenue = 0;
    let orderCount = 0;

    const now = new Date();
    const thisMonthStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    ).getTime();
    const lastMonthStart = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1
    ).getTime();
    const lastMonthEnd = thisMonthStart - 1;

    for (const tx of transactions) {
      // Only count purchase transactions with pricePaid
      if (tx.type === "purchase" && tx.status === "completed" && tx.pricePaid) {
        const revenue = tx.pricePaid; // Already in pence
        totalRevenue += revenue;
        orderCount++;

        if (tx.createdAt >= thisMonthStart) {
          revenueThisMonth += revenue;
        }
        if (tx.createdAt >= lastMonthStart && tx.createdAt <= lastMonthEnd) {
          revenueLastMonth += revenue;
        }

        // Token purchases vs subscription (check packageId pattern)
        if (
          tx.packageId?.startsWith("sub_") ||
          tx.description?.toLowerCase().includes("subscription")
        ) {
          subscriptionRevenue += revenue;
        } else {
          tokenPurchaseRevenue += revenue;
        }
      }
    }

    // Calculate MRR from active subscriptions
    const subscriptions = await this.getSubscriptionPackages();
    const mrr = subscriptions.reduce((sum, pkg) => {
      return (
        sum + (pkg.activeSubscribers ?? 0) * ((pkg.priceMonthlyGbp ?? 0) / 100)
      );
    }, 0);

    return {
      totalRevenue,
      revenueThisMonth,
      revenueLastMonth,
      averageOrderValue: orderCount > 0 ? totalRevenue / orderCount : 0,
      mrr,
      tokenPurchaseRevenue,
      subscriptionRevenue,
    };
  }

  async getRevenueChartData(
    days: number = 30
  ): Promise<RevenueChartDataPoint[]> {
    // Fetch all transactions from /transactions/{uid} structure
    const transactions = await this.getAllUserTransactions(10000);

    const now = new Date();
    const isHourly = days === 1;

    if (isHourly) {
      // For 1d view, group by hour
      const dataMap = new Map<
        string,
        { tokenPurchases: number; subscriptions: number }
      >();

      // Initialize all 24 hours
      for (let i = 23; i >= 0; i--) {
        const hourDate = new Date(now);
        hourDate.setHours(now.getHours() - i, 0, 0, 0);
        const hourKey = hourDate.toISOString();
        dataMap.set(hourKey, { tokenPurchases: 0, subscriptions: 0 });
      }

      const cutoff = now.getTime() - 24 * 60 * 60 * 1000;

      for (const tx of transactions) {
        if (
          tx.type !== "purchase" ||
          tx.status !== "completed" ||
          !tx.pricePaid
        )
          continue;
        if (tx.createdAt < cutoff) continue;

        const txDate = new Date(tx.createdAt);

        // Find matching hour bucket
        const keys = Array.from(dataMap.keys());
        for (const key of keys) {
          const keyDate = new Date(key);
          if (
            keyDate.getHours() === txDate.getHours() &&
            keyDate.getDate() === txDate.getDate()
          ) {
            const data = dataMap.get(key)!;
            if (
              tx.packageId?.startsWith("sub_") ||
              tx.description?.toLowerCase().includes("subscription")
            ) {
              data.subscriptions += tx.pricePaid;
            } else {
              data.tokenPurchases += tx.pricePaid;
            }
            break;
          }
        }
      }

      return Array.from(dataMap).map(([date, data]) => ({
        date,
        tokenPurchases: data.tokenPurchases,
        subscriptions: data.subscriptions,
        total: data.tokenPurchases + data.subscriptions,
      }));
    } else {
      // For other views, group by day
      const dataMap = new Map<
        string,
        { tokenPurchases: number; subscriptions: number }
      >();

      // Initialize all days
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split("T")[0];
        dataMap.set(dateStr, { tokenPurchases: 0, subscriptions: 0 });
      }

      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - days);
      const cutoffTimestamp = cutoff.getTime();

      for (const tx of transactions) {
        if (
          tx.type !== "purchase" ||
          tx.status !== "completed" ||
          !tx.pricePaid
        )
          continue;
        if (tx.createdAt < cutoffTimestamp) continue;

        const dateStr = new Date(tx.createdAt).toISOString().split("T")[0];

        if (dataMap.has(dateStr)) {
          const data = dataMap.get(dateStr)!;
          if (
            tx.packageId?.startsWith("sub_") ||
            tx.description?.toLowerCase().includes("subscription")
          ) {
            data.subscriptions += tx.pricePaid;
          } else {
            data.tokenPurchases += tx.pricePaid;
          }
        }
      }

      return Array.from(dataMap).map(([date, data]) => ({
        date,
        tokenPurchases: data.tokenPurchases,
        subscriptions: data.subscriptions,
        total: data.tokenPurchases + data.subscriptions,
      }));
    }
  }

  // ============ PROMO CODES ============

  async getPromoCodes(): Promise<PromoCode[]> {
    // New structure: admin/promo_codes
    const promoPath = this.testMode
      ? "testMode/admin/promo_codes"
      : "admin/promo_codes";
    const codesRef = ref(db, promoPath);
    const snapshot = await get(codesRef);
    const codes: PromoCode[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        codes.push({ id: child.key!, ...child.val() } as PromoCode);
      });
    }

    return codes.sort((a, b) => b.createdAt - a.createdAt);
  }

  async createPromoCode(
    params: CreatePromoCodeParams,
    adminId: string
  ): Promise<PromoCode> {
    // Call Cloud Function to create Stripe Coupon/PromotionCode and save to Firebase
    const createPromoCodeFn = httpsCallable<
      {
        code?: string;
        description: string;
        discountType: "percentage" | "fixed_amount" | "free_trial";
        discountValue: number;
        freeTrialDays?: number;
        applicableTo: "all" | "subscriptions" | "tokens" | "specific";
        specificPackageIds?: string[];
        maxRedemptions?: number;
        validFrom?: number;
        validUntil?: number;
        testMode: boolean;
      },
      {
        id: string;
        code: string;
        stripeCouponId: string;
        stripePromotionCodeId: string;
      }
    >(functions, "createPromoCode");

    console.log("[AdminService] Calling createPromoCode Cloud Function", {
      params,
      testMode: this.testMode,
    });

    try {
      const result = await createPromoCodeFn({
        code: params.code,
        description: params.description,
        discountType: params.discountType,
        discountValue: params.discountValue,
        freeTrialDays: params.freeTrialDays,
        applicableTo: params.applicableTo,
        specificPackageIds: params.specificPackageIds,
        maxRedemptions: params.maxRedemptions,
        validFrom: params.validFrom,
        validUntil: params.validUntil,
        testMode: this.testMode,
      });

      console.log("[AdminService] createPromoCode result:", result.data);

      // Fetch the created promo code from Firebase to return full data
      const promoPath = this.testMode
        ? `testMode/admin/promo_codes/${result.data.id}`
        : `admin/promo_codes/${result.data.id}`;

      // Persist optional per-language description translation as a subdoc
      // on the same record. The promo `code` itself is never translated.
      if (params.translations) {
        await update(ref(db, promoPath), {
          translations: params.translations,
        });
      }

      const promoRef = ref(db, promoPath);
      const snapshot = await get(promoRef);

      if (!snapshot.exists()) {
        throw new Error("Promo code created but not found in database");
      }

      return { id: result.data.id, ...snapshot.val() } as PromoCode;
    } catch (error) {
      console.error("[AdminService] createPromoCode error:", error);
      throw new Error(getErrorMessage(error));
    }
  }

  async updatePromoCode(
    id: string,
    updates: Partial<PromoCode>,
    adminId: string
  ): Promise<void> {
    const promoPath = this.testMode
      ? `testMode/admin/promo_codes/${id}`
      : `admin/promo_codes/${id}`;
    await update(ref(db, promoPath), {
      ...updates,
      updatedAt: Date.now(),
    });

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "update_promo_code",
      targetType: "promo_code",
      targetId: id,
      details: updates,
    });
  }

  async deactivatePromoCode(id: string, adminId: string): Promise<void> {
    // Call Cloud Function to deactivate in Stripe and Firebase
    const deactivatePromoCodeFn = httpsCallable<
      { promoCodeId: string; testMode: boolean; active: boolean },
      { success: boolean }
    >(functions, "deactivatePromoCode");

    console.log("[AdminService] Calling deactivatePromoCode Cloud Function", {
      id,
      testMode: this.testMode,
    });

    try {
      await deactivatePromoCodeFn({
        promoCodeId: id,
        testMode: this.testMode,
        active: false,
      });

      console.log("[AdminService] deactivatePromoCode success", { id });
    } catch (error) {
      console.error("[AdminService] deactivatePromoCode error:", error);
      throw new Error(getErrorMessage(error));
    }
  }

  async activatePromoCode(id: string, adminId: string): Promise<void> {
    // Call Cloud Function to deactivate in Stripe and Firebase
    const activatePromoCodeFn = httpsCallable<
      { promoCodeId: string; testMode: boolean; active: boolean },
      { success: boolean }
    >(functions, "deactivatePromoCode");

    console.log("[AdminService] Calling deactivatePromoCode Cloud Function", {
      id,
      testMode: this.testMode,
    });

    try {
      await activatePromoCodeFn({
        promoCodeId: id,
        testMode: this.testMode,
        active: true,
      });

      console.log("[AdminService] deactivatePromoCode success", { id });
    } catch (error) {
      console.error("[AdminService] deactivatePromoCode error:", error);
      throw new Error(getErrorMessage(error));
    }
  }

  private generatePromoCode(): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  // ============ MEDIA MANAGEMENT ============

  async getMediaKits(): Promise<MediaKit[]> {
    // New structure: media/kits
    const kitsRef = ref(db, "media/kits");
    const snapshot = await get(kitsRef);
    const kits: MediaKit[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        kits.push({ id: child.key!, ...child.val() } as MediaKit);
      });
    }

    return kits.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async getMediaExamples(): Promise<MediaExample[]> {
    // New structure: media/examples
    const examplesRef = ref(db, "media/examples");
    const snapshot = await get(examplesRef);
    const examples: MediaExample[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        examples.push({ id: child.key!, ...child.val() } as MediaExample);
      });
    }

    return examples.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async uploadMedia(
    params: UploadMediaParams,
    adminId: string
  ): Promise<MediaKit | MediaExample> {
    const {
      file,
      type,
      name,
      description,
      prompt,
      teamName,
      category,
      tags,
      accessTiers,
    } = params;

    // Generate ID - new structure: media/kits or media/examples
    const collectionRef = ref(
      db,
      type === "kit" ? "media/kits" : "media/examples"
    );
    const newRef = push(collectionRef);
    const mediaId = newRef.key!;

    // Get file extension
    const fileExtension = file.name.split(".").pop() || "png";

    // Upload to Firebase Storage - flat structure: media/kits/{id}.{ext} or media/examples/{id}.{ext}
    const storagePath = `media/${type}s/${mediaId}.${fileExtension}`;
    const fileRef = storageRef(storage, storagePath);
    await uploadBytes(fileRef, file, {
      contentType: file.type || "image/png",
    });
    const imageUrl = await getDownloadURL(fileRef);

    // Map access tiers to entitlement IDs
    const entitlementIds = accessTiers.filter(
      (t): t is Exclude<typeof t, "all"> => t !== "all"
    );

    const now = Date.now();
    const baseData: Record<string, unknown> = {
      type,
      name,
      description: description || "",
      prompt: prompt || "", // AI generation prompt
      imageUrl,
      storagePath,
      accessTiers,
      entitlementIds,
      category,
      tags: tags || [],
      sortOrder: 0,
      isActive: true,
      showOnHero: false,
      aspectRatio: params.aspectRatio || "3:4",
      resolution: params.resolution || "2K",
      gender: params.gender || "unisex",
      metadata: {
        fileSize: file.size,
        mimeType: file.type,
      },
      createdAt: now,
      createdBy: adminId,
      updatedAt: now,
    };
    if (params.translations) {
      // Persist as a subdoc on the same record so reads/writes stay atomic.
      baseData.translations = params.translations;
    }

    if (type === "kit") {
      const kitData = {
        ...baseData,
        type: "kit",
        teamName: teamName || "",
      } as Omit<MediaKit, "id">;
      await set(newRef, kitData);

      await this.logAdminAction({
        adminId,
        adminEmail: "",
        action: "upload_media_kit",
        targetType: "media",
        targetId: mediaId,
        details: { name, category },
      });

      return { id: mediaId, ...kitData };
    } else {
      const exampleData = {
        ...baseData,
        type: "example",
      } as Omit<MediaExample, "id">;
      await set(newRef, exampleData);

      await this.logAdminAction({
        adminId,
        adminEmail: "",
        action: "upload_media_example",
        targetType: "media",
        targetId: mediaId,
        details: { name, category },
      });

      return { id: mediaId, ...exampleData };
    }
  }

  async updateMedia(params: UpdateMediaParams, adminId: string): Promise<void> {
    const { id, ...updates } = params;

    // Determine collection from existing data - new structure: media/kits or media/examples
    let collection = "media/kits";
    const kitRef = ref(db, `media/kits/${id}`);
    const kitSnapshot = await get(kitRef);
    if (!kitSnapshot.exists()) {
      collection = "media/examples";
    }

    // Map access tiers to entitlement IDs if accessTiers is being updated
    let entitlementIds;
    if (updates.accessTiers) {
      entitlementIds = updates.accessTiers.filter(
        (t): t is Exclude<typeof t, "all"> => t !== "all"
      );
    }

    // Filter out undefined values to prevent Firebase errors
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([, value]) => value !== undefined)
    );

    // Ensure tags is always an array if present
    if ("tags" in cleanUpdates && !Array.isArray(cleanUpdates.tags)) {
      cleanUpdates.tags = [];
    }

    await update(ref(db, `${collection}/${id}`), {
      ...cleanUpdates,
      ...(entitlementIds && { entitlementIds }),
      updatedAt: Date.now(),
    });

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "update_media",
      targetType: "media",
      targetId: id,
      details: cleanUpdates,
    });
  }

  /**
   * Upload a jersey/kit with explicit front + back images and a name.
   * Stored at media/kits/{id} alongside the existing kits collection so
   * downstream consumers (event templates, the mobile kit picker) can
   * reference it by id. The legacy `imageUrl` field is mirrored to the
   * front image so older consumers that only know about `imageUrl`
   * continue to render correctly.
   */
  async uploadMediaJersey(
    params: {
      name: string;
      teamName?: string;
      frontFile: File;
      backFile: File;
    },
    adminId: string
  ): Promise<MediaKit> {
    const { name, teamName, frontFile, backFile } = params;
    if (!name?.trim()) throw new Error("Jersey name is required");
    if (!frontFile) throw new Error("Front image is required");
    if (!backFile) throw new Error("Back image is required");

    const collectionRef = ref(db, "media/kits");
    const newRef = push(collectionRef);
    const id = newRef.key!;

    const frontExt = frontFile.name.split(".").pop() || "png";
    const backExt = backFile.name.split(".").pop() || "png";
    const frontPath = `media/kits/${id}_front.${frontExt}`;
    const backPath = `media/kits/${id}_back.${backExt}`;

    const frontRef = storageRef(storage, frontPath);
    const backRef = storageRef(storage, backPath);
    await uploadBytes(frontRef, frontFile, {
      contentType: frontFile.type || "image/png",
    });
    await uploadBytes(backRef, backFile, {
      contentType: backFile.type || "image/png",
    });
    const [frontImageUrl, backImageUrl] = await Promise.all([
      getDownloadURL(frontRef),
      getDownloadURL(backRef),
    ]);

    const now = Date.now();
    const kitData: Omit<MediaKit, "id"> = {
      type: "kit",
      name: name.trim(),
      description: "",
      teamName: teamName?.trim() || "",
      // Legacy single-image field — mirror the front so older readers keep
      // working without a data migration.
      imageUrl: frontImageUrl,
      storagePath: frontPath,
      frontImageUrl,
      frontStoragePath: frontPath,
      backImageUrl,
      backStoragePath: backPath,
      accessTiers: ["all"],
      entitlementIds: [],
      category: "Jerseys",
      tags: [],
      sortOrder: 0,
      isActive: true,
      showOnHero: false,
      aspectRatio: "3:4",
      resolution: "2K",
      gender: "unisex",
      metadata: {
        fileSize: frontFile.size + backFile.size,
        mimeType: frontFile.type || "image/png",
      },
      createdAt: now,
      createdBy: adminId,
      updatedAt: now,
    };
    await set(newRef, kitData);

    // Firebase RTDB rejects undefined values, so only include teamName
    // in the audit log when the admin actually entered one.
    const logDetails: Record<string, unknown> = { name };
    if (teamName) logDetails.teamName = teamName;
    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "upload_media_jersey",
      targetType: "media",
      targetId: id,
      details: logDetails,
    });

    return { id, ...kitData };
  }

  async deleteMedia(
    id: string,
    type: "kit" | "example",
    adminId: string
  ): Promise<void> {
    // New structure: media/kits or media/examples
    const collection = type === "kit" ? "media/kits" : "media/examples";
    const mediaRef = ref(db, `${collection}/${id}`);
    const snapshot = await get(mediaRef);

    if (snapshot.exists()) {
      const media = snapshot.val();

      // Collect every storage path we know about so jerseys (front + back)
      // get fully cleaned up alongside legacy single-image records.
      // Set<string> + for-of would require ES2015 iteration; an object-keyed
      // map keeps it ES5-safe for the older tsconfig target.
      const paths: Record<string, true> = {};
      if (media.storagePath) paths[media.storagePath as string] = true;
      if (media.frontStoragePath)
        paths[media.frontStoragePath as string] = true;
      if (media.backStoragePath) paths[media.backStoragePath as string] = true;
      if (media.thumbnailStoragePath)
        paths[media.thumbnailStoragePath as string] = true;
      for (const p of Object.keys(paths)) {
        try {
          await deleteObject(storageRef(storage, p));
        } catch (e) {
          console.error("Failed to delete file from storage:", p, e);
        }
      }

      // Delete from database
      await set(mediaRef, null);

      await this.logAdminAction({
        adminId,
        adminEmail: "",
        action: "delete_media",
        targetType: "media",
        targetId: id,
        details: { type, name: media.name },
      });
    }
  }

  // ============ ADMIN ACTION LOG ============

  private async logAdminAction(
    log: Omit<AdminActionLog, "id" | "createdAt">
  ): Promise<void> {
    const logsRef = ref(db, "admin_action_logs");
    const newRef = push(logsRef);

    await set(newRef, {
      ...log,
      createdAt: Date.now(),
    });
  }

  async getAdminActionLogs(limit: number = 100): Promise<AdminActionLog[]> {
    const logsRef = ref(db, "admin_action_logs");
    const logsQuery = query(
      logsRef,
      orderByChild("createdAt"),
      limitToFirst(limit)
    );
    const snapshot = await get(logsQuery);
    const logs: AdminActionLog[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        logs.push({ id: child.key!, ...child.val() } as AdminActionLog);
      });
    }

    return logs.sort((a, b) => b.createdAt - a.createdAt);
  }

  // ============ TEAMS ============

  async getTeams(): Promise<string[]> {
    const basePath = this.testMode ? "testMode" : "";
    const teamsRef = ref(db, `${basePath ? basePath + "/" : ""}teams`);
    const snapshot = await get(teamsRef);

    const teams: string[] = [];
    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        teams.push(child.val().name);
      });
    }
    return teams.sort((a, b) => a.localeCompare(b));
  }

  async createTeam(name: string): Promise<void> {
    const basePath = this.testMode ? "testMode" : "";
    const teamsRef = ref(db, `${basePath ? basePath + "/" : ""}teams`);
    const newRef = push(teamsRef);
    await set(newRef, {
      name,
      createdAt: Date.now(),
    });
  }

  // ============ CATEGORIES (for examples) ============

  async getCategories(): Promise<string[]> {
    const categoriesRef = ref(db, "example_categories");
    const snapshot = await get(categoriesRef);

    const categories: string[] = [];
    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        categories.push(child.val().name);
      });
    }
    return categories.sort((a, b) => a.localeCompare(b));
  }

  async createCategory(name: string): Promise<void> {
    const categoriesRef = ref(db, "example_categories");
    const newRef = push(categoriesRef);
    await set(newRef, {
      name,
      createdAt: Date.now(),
    });
  }

  async getActiveMediaExamples(): Promise<MediaExample[]> {
    const examples = await this.getMediaExamples();
    return examples.filter((e) => e.isActive);
  }

  async getHeroMediaKits(): Promise<MediaKit[]> {
    const kits = await this.getMediaKits();
    return kits.filter((k) => k.isActive && k.showOnHero);
  }

  async getHeroMediaExamples(): Promise<MediaExample[]> {
    const examples = await this.getMediaExamples();
    return examples.filter((e) => e.isActive && e.showOnHero);
  }

  // ============ REDEMPTIONS ============

  async getRedemptions(
    filters?: RedemptionFilters,
    pageSize: number = 50
  ): Promise<{ redemptions: TokenRedemption[]; hasMore: boolean }> {
    const basePath = this.testMode ? "testMode" : "";
    const redemptionsRef = ref(
      db,
      `${basePath ? basePath + "/" : ""}redemptions`
    );
    let redemptionsQuery = query(
      redemptionsRef,
      orderByChild("createdAt"),
      limitToFirst(pageSize + 1)
    );

    const snapshot = await get(redemptionsQuery);
    const redemptions: TokenRedemption[] = [];

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        const redemption = {
          id: child.key!,
          ...child.val(),
        } as TokenRedemption;

        // Apply filters
        if (filters) {
          if (filters.userId && redemption.userId !== filters.userId) return;
          if (filters.type && redemption.type !== filters.type) return;
          if (filters.status && redemption.status !== filters.status) return;
          if (filters.startDate && redemption.createdAt < filters.startDate)
            return;
          if (filters.endDate && redemption.createdAt > filters.endDate) return;
          if (filters.minTokens && redemption.tokensUsed < filters.minTokens)
            return;
          if (filters.maxTokens && redemption.tokensUsed > filters.maxTokens)
            return;
        }

        redemptions.push(redemption);
      });
    }

    // Sort by createdAt descending (most recent first)
    redemptions.sort((a, b) => b.createdAt - a.createdAt);

    const hasMore = redemptions.length > pageSize;
    return {
      redemptions: redemptions.slice(0, pageSize),
      hasMore,
    };
  }

  async getRedemptionStats(): Promise<RedemptionStats> {
    const basePath = this.testMode ? "testMode" : "";
    const redemptionsRef = ref(
      db,
      `${basePath ? basePath + "/" : ""}redemptions`
    );
    const snapshot = await get(redemptionsRef);

    const now = Date.now();
    const todayStart = new Date().setHours(0, 0, 0, 0);
    const weekStart = now - 7 * 24 * 60 * 60 * 1000;
    const monthStart = now - 30 * 24 * 60 * 60 * 1000;

    const stats: RedemptionStats = {
      totalRedemptions: 0,
      totalTokensRedeemed: 0,
      redemptionsToday: 0,
      redemptionsThisWeek: 0,
      redemptionsThisMonth: 0,
      byType: {} as Record<RedemptionType, { count: number; tokens: number }>,
      averageTokensPerRedemption: 0,
    };

    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        const redemption = child.val() as TokenRedemption;
        if (redemption.status !== "completed") return;

        stats.totalRedemptions++;
        stats.totalTokensRedeemed += redemption.tokensUsed;

        if (redemption.createdAt >= todayStart) stats.redemptionsToday++;
        if (redemption.createdAt >= weekStart) stats.redemptionsThisWeek++;
        if (redemption.createdAt >= monthStart) stats.redemptionsThisMonth++;

        // By type
        if (!stats.byType[redemption.type]) {
          stats.byType[redemption.type] = { count: 0, tokens: 0 };
        }
        stats.byType[redemption.type].count++;
        stats.byType[redemption.type].tokens += redemption.tokensUsed;
      });
    }

    stats.averageTokensPerRedemption =
      stats.totalRedemptions > 0
        ? Math.round(stats.totalTokensRedeemed / stats.totalRedemptions)
        : 0;

    return stats;
  }

  async refundRedemption(
    redemptionId: string,
    reason: string,
    adminId: string
  ): Promise<void> {
    const basePath = this.testMode ? "testMode" : "";
    const redemptionRef = ref(
      db,
      `${basePath ? basePath + "/" : ""}redemptions/${redemptionId}`
    );
    const snapshot = await get(redemptionRef);

    if (!snapshot.exists()) {
      throw new Error("Redemption not found");
    }

    const redemption = snapshot.val() as TokenRedemption;

    if (redemption.status === "refunded") {
      throw new Error("Redemption already refunded");
    }

    // Update redemption status
    await update(redemptionRef, {
      status: "refunded",
      refundReason: reason,
      refundedAt: Date.now(),
      refundedBy: adminId,
    });

    // Return tokens to user's wallet - new structure: user_data/{uid}/wallet
    const walletRef = ref(
      db,
      `${basePath ? basePath + "/" : ""}user_data/${redemption.userId}/wallet`
    );
    const walletSnapshot = await get(walletRef);

    if (walletSnapshot.exists()) {
      const wallet = walletSnapshot.val();
      await update(walletRef, {
        balance: (wallet.balance || 0) + redemption.tokensUsed,
        updatedAt: Date.now(),
      });
    }

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "refund_redemption",
      targetType: "redemption",
      targetId: redemptionId,
      details: {
        userId: redemption.userId,
        tokensRefunded: redemption.tokensUsed,
        reason,
      },
    });
  }

  // ============ LANGUAGES ============

  /**
   * Read the supported languages from `admin/languages/{code}` in RTDB. This
   * collection is owned by the Languages admin page; if it hasn't shipped
   * yet, callers fall back to ["en"] (English-only).
   *
   * Note: language metadata is global config (not test-mode-prefixed) -- the
   * same set of supported languages applies to both prod and test mode.
   */
  async getLanguages(): Promise<LanguageEntry[]> {
    const langRef = ref(db, "admin/languages");
    const snapshot = await get(langRef);
    const langs: LanguageEntry[] = [];
    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        const val = child.val();
        // Each entry is a SupportedLanguage record. Accept the flat key as
        // the code if `code` isn't in the value (legacy data shape).
        if (val && typeof val === "object") {
          langs.push({
            code: val.code || child.key!,
            name: val.name || child.key!,
            isActive: val.isActive !== false,
          });
        }
      });
    }
    return langs;
  }

  // ============ COMMUNICATIONS / CAMPAIGNS ============

  private campaignsPath(): string {
    return this.testMode ? "testMode/admin/campaigns" : "admin/campaigns";
  }

  private campaignStoragePath(campaignId: string, ext: string): string {
    const ts = Date.now();
    const prefix = this.testMode ? "testMode/" : "";
    return `${prefix}campaigns/${campaignId}/image_${ts}.${ext}`;
  }

  private campaignNotificationStoragePath(
    campaignId: string,
    ext: string
  ): string {
    const ts = Date.now();
    const prefix = this.testMode ? "testMode/" : "";
    return `${prefix}campaigns/${campaignId}/notif_${ts}.${ext}`;
  }

  /**
   * Upload a rich-image for a campaign push and return the storage metadata.
   * Best-effort: callers must handle the rejection and surface the error;
   * we do not delete the file on RTDB-write failure (an admin re-save will
   * overwrite the path, and the orphan can be cleaned by a maintenance job).
   */
  private async uploadCampaignNotificationImage(
    campaignId: string,
    file: File
  ): Promise<{ imageUrl: string; imageStoragePath: string }> {
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const storagePath = this.campaignNotificationStoragePath(campaignId, ext);
    const fileRef = storageRef(storage, storagePath);
    await uploadBytes(fileRef, file, {
      contentType: file.type || "image/jpeg",
    });
    const imageUrl = await getDownloadURL(fileRef);
    return { imageUrl, imageStoragePath: storagePath };
  }

  async getCampaigns(): Promise<Campaign[]> {
    const campaignsRef = ref(db, this.campaignsPath());
    const snapshot = await get(campaignsRef);
    const campaigns: Campaign[] = [];
    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        campaigns.push({ id: child.key!, ...child.val() } as Campaign);
      });
    }
    // Sort by startAt ascending, but show upcoming/active first then ended.
    return campaigns.sort((a, b) => a.startAtMs - b.startAtMs);
  }

  async getCampaign(id: string): Promise<Campaign | null> {
    const snapshot = await get(ref(db, `${this.campaignsPath()}/${id}`));
    if (!snapshot.exists()) return null;
    return { id, ...snapshot.val() } as Campaign;
  }

  async createCampaign(
    params: CampaignFormParams,
    adminId: string
  ): Promise<Campaign> {
    if (!params.imageFile) {
      throw new Error("An image is required to create a campaign.");
    }
    if (!params.translations.en?.title?.trim()) {
      throw new Error("English title is required.");
    }
    if (!isValidUrl(params.buttonLink)) {
      throw new Error("Button link must be a valid URL.");
    }

    const startAtMs = amsterdamLocalToUtcMs(params.startAtLocal);
    const endAtMs = amsterdamLocalToUtcMs(params.endAtLocal);
    if (!Number.isFinite(startAtMs) || !Number.isFinite(endAtMs)) {
      throw new Error("Start and end datetimes are required.");
    }
    if (endAtMs <= startAtMs) {
      throw new Error("End datetime must be after the start datetime.");
    }

    // Reserve a campaign id first so the storage path can include it. Image
    // is uploaded BEFORE the RTDB record is written, so an upload failure
    // doesn't leave an orphan record without an image.
    const collectionRef = ref(db, this.campaignsPath());
    const newRef = push(collectionRef);
    const id = newRef.key!;

    const file = params.imageFile;
    const ext = (file.name.split(".").pop() || "png").toLowerCase();
    const storagePath = this.campaignStoragePath(id, ext);
    const fileRef = storageRef(storage, storagePath);
    await uploadBytes(fileRef, file, {
      contentType: file.type || "image/png",
    });
    const imageUrl = await getDownloadURL(fileRef);

    // Resolve the optional notification block. If a new image File is
    // supplied, upload it first so a failed upload aborts the create cleanly.
    const notification = await this.resolveNotificationForWrite(
      id,
      params.notification,
      params.notificationImageFile,
      undefined
    );

    const now = Date.now();
    const data: Omit<Campaign, "id"> = {
      imageUrl,
      imageStoragePath: storagePath,
      translations: sanitizeTranslations(params.translations),
      buttonLink: params.buttonLink.trim(),
      startAtMs,
      endAtMs,
      startAtLocal: params.startAtLocal,
      endAtLocal: params.endAtLocal,
      isActive: params.isActive,
      createdAt: now,
      createdBy: adminId,
      updatedAt: now,
      updatedBy: adminId,
      ...(notification ? { notification } : {}),
    };

    await set(newRef, data);

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "create_campaign",
      targetType: "campaign",
      targetId: id,
      details: { startAtMs, endAtMs, isActive: params.isActive },
    });

    return { id, ...data };
  }

  async updateCampaign(
    id: string,
    params: CampaignFormParams,
    adminId: string
  ): Promise<void> {
    if (!params.translations.en?.title?.trim()) {
      throw new Error("English title is required.");
    }
    if (!isValidUrl(params.buttonLink)) {
      throw new Error("Button link must be a valid URL.");
    }

    const startAtMs = amsterdamLocalToUtcMs(params.startAtLocal);
    const endAtMs = amsterdamLocalToUtcMs(params.endAtLocal);
    if (!Number.isFinite(startAtMs) || !Number.isFinite(endAtMs)) {
      throw new Error("Start and end datetimes are required.");
    }
    if (endAtMs <= startAtMs) {
      throw new Error("End datetime must be after the start datetime.");
    }

    const existingSnap = await get(ref(db, `${this.campaignsPath()}/${id}`));
    if (!existingSnap.exists()) {
      throw new Error("Campaign not found.");
    }
    const existing = existingSnap.val() as Campaign;

    let imageUrl = existing.imageUrl;
    let imageStoragePath = existing.imageStoragePath;
    let oldStoragePath: string | null = null;

    // If a new image was supplied, upload it first; only swap RTDB record
    // after a successful upload, then delete the old asset.
    if (params.imageFile) {
      const file = params.imageFile;
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const newStoragePath = this.campaignStoragePath(id, ext);
      const fileRef = storageRef(storage, newStoragePath);
      await uploadBytes(fileRef, file, {
        contentType: file.type || "image/png",
      });
      imageUrl = await getDownloadURL(fileRef);
      oldStoragePath = imageStoragePath || null;
      imageStoragePath = newStoragePath;
    }

    const notification = await this.resolveNotificationForWrite(
      id,
      params.notification,
      params.notificationImageFile,
      existing.notification
    );

    const now = Date.now();
    // RTDB `update` treats explicit `null` as "delete this child". We use
    // that to remove the notification block when the admin disables push
    // for an existing campaign.
    const updates: Record<string, unknown> = {
      imageUrl,
      imageStoragePath,
      translations: sanitizeTranslations(params.translations),
      buttonLink: params.buttonLink.trim(),
      startAtMs,
      endAtMs,
      startAtLocal: params.startAtLocal,
      endAtLocal: params.endAtLocal,
      isActive: params.isActive,
      notification: notification ?? null,
      updatedAt: now,
      updatedBy: adminId,
    };
    await update(ref(db, `${this.campaignsPath()}/${id}`), updates);

    // Best-effort cleanup of the previous notification image when the admin
    // either replaced it OR disabled push entirely.
    if (
      existing.notification?.imageStoragePath &&
      existing.notification.imageStoragePath !== notification?.imageStoragePath
    ) {
      try {
        await deleteObject(
          storageRef(storage, existing.notification.imageStoragePath)
        );
      } catch (err) {
        console.warn(
          "[AdminService] Failed to delete old notification image:",
          err
        );
      }
    }

    // Best-effort cleanup of the previous image after we've safely written
    // the new path. If this fails, the new record is already correct -- the
    // old object becomes orphaned and can be cleaned by a maintenance job.
    if (oldStoragePath) {
      try {
        await deleteObject(storageRef(storage, oldStoragePath));
      } catch (err) {
        console.warn(
          "[AdminService] Failed to delete old campaign image:",
          err
        );
      }
    }

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "update_campaign",
      targetType: "campaign",
      targetId: id,
      details: { startAtMs, endAtMs, isActive: params.isActive },
    });
  }

  // ============ EVENT TEMPLATES ============

  private eventTemplatesPath(): string {
    return this.testMode
      ? "testMode/admin/event_templates"
      : "admin/event_templates";
  }

  private eventTemplateStoragePath(eventId: string, ext: string): string {
    const ts = Date.now();
    const prefix = this.testMode ? "testMode/" : "";
    return `${prefix}event_templates/${eventId}/cover_${ts}.${ext}`;
  }

  async getEventTemplates(): Promise<EventTemplate[]> {
    const eventsRef = ref(db, this.eventTemplatesPath());
    const snapshot = await get(eventsRef);
    const events: EventTemplate[] = [];
    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        events.push({ id: child.key!, ...child.val() } as EventTemplate);
      });
    }
    return events.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async getEventTemplate(id: string): Promise<EventTemplate | null> {
    const snapshot = await get(ref(db, `${this.eventTemplatesPath()}/${id}`));
    if (!snapshot.exists()) return null;
    return { id, ...snapshot.val() } as EventTemplate;
  }

  async createEventTemplate(
    params: EventTemplateFormParams,
    adminId: string
  ): Promise<EventTemplate> {
    validateEventTemplateForm(params, /* requireImage */ true);

    const startAtMs = params.startAtLocal
      ? amsterdamLocalToUtcMs(params.startAtLocal)
      : null;
    const endAtMs = params.endAtLocal
      ? amsterdamLocalToUtcMs(params.endAtLocal)
      : null;
    if (
      startAtMs !== null &&
      endAtMs !== null &&
      Number.isFinite(startAtMs) &&
      Number.isFinite(endAtMs) &&
      endAtMs <= startAtMs
    ) {
      throw new Error("End datetime must be after the start datetime.");
    }
    if (
      (params.startAtLocal && !Number.isFinite(startAtMs as number)) ||
      (params.endAtLocal && !Number.isFinite(endAtMs as number))
    ) {
      throw new Error("Schedule datetime is invalid.");
    }

    // Reserve an id first so the storage path can include it.
    const collectionRef = ref(db, this.eventTemplatesPath());
    const newRef = push(collectionRef);
    const id = newRef.key!;

    const file = params.imageFile!;
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const storagePath = this.eventTemplateStoragePath(id, ext);
    const fileRef = storageRef(storage, storagePath);
    await uploadBytes(fileRef, file, {
      contentType: file.type || "image/jpeg",
    });
    const imageUrl = await getDownloadURL(fileRef);

    const now = Date.now();
    const data: Omit<EventTemplate, "id"> = {
      imageUrl,
      imageStoragePath: storagePath,
      translations: sanitizeEventTranslations(params.translations),
      inspirationImageIds: [...params.inspirationImageIds],
      linkedJerseyIds: [...(params.linkedJerseyIds || [])],
      pricing: {
        priceUsd: params.pricing.priceUsd,
        priceGbp: params.pricing.priceGbp,
        priceEur: params.pricing.priceEur,
      },
      appStoreProductId: params.appStoreProductId.trim(),
      playStoreProductId: params.playStoreProductId.trim(),
      stripeProductId: params.stripeProductId.trim(),
      isActive: params.isActive,
      startAtMs:
        startAtMs !== null && Number.isFinite(startAtMs) ? startAtMs : null,
      endAtMs: endAtMs !== null && Number.isFinite(endAtMs) ? endAtMs : null,
      startAtLocal: params.startAtLocal || null,
      endAtLocal: params.endAtLocal || null,
      sortOrder: params.sortOrder,
      createdAt: now,
      createdBy: adminId,
      updatedAt: now,
      updatedBy: adminId,
    };

    await set(newRef, data);

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "create_event_template",
      targetType: "media",
      targetId: id,
      details: {
        startAtMs: data.startAtMs,
        endAtMs: data.endAtMs,
        isActive: data.isActive,
        inspirationCount: data.inspirationImageIds.length,
      },
    });

    return { id, ...data };
  }

  async updateEventTemplate(
    id: string,
    params: EventTemplateFormParams,
    adminId: string
  ): Promise<void> {
    validateEventTemplateForm(params, /* requireImage */ false);

    const startAtMs = params.startAtLocal
      ? amsterdamLocalToUtcMs(params.startAtLocal)
      : null;
    const endAtMs = params.endAtLocal
      ? amsterdamLocalToUtcMs(params.endAtLocal)
      : null;
    if (
      startAtMs !== null &&
      endAtMs !== null &&
      Number.isFinite(startAtMs) &&
      Number.isFinite(endAtMs) &&
      endAtMs <= startAtMs
    ) {
      throw new Error("End datetime must be after the start datetime.");
    }

    const existingSnap = await get(
      ref(db, `${this.eventTemplatesPath()}/${id}`)
    );
    if (!existingSnap.exists()) {
      throw new Error("Event template not found.");
    }
    const existing = existingSnap.val() as EventTemplate;

    let imageUrl = existing.imageUrl;
    let imageStoragePath = existing.imageStoragePath;
    let oldStoragePath: string | null = null;

    if (params.imageFile) {
      const file = params.imageFile;
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const newStoragePath = this.eventTemplateStoragePath(id, ext);
      const fileRef = storageRef(storage, newStoragePath);
      await uploadBytes(fileRef, file, {
        contentType: file.type || "image/jpeg",
      });
      imageUrl = await getDownloadURL(fileRef);
      oldStoragePath = imageStoragePath || null;
      imageStoragePath = newStoragePath;
    }

    const now = Date.now();
    await update(ref(db, `${this.eventTemplatesPath()}/${id}`), {
      imageUrl,
      imageStoragePath,
      translations: sanitizeEventTranslations(params.translations),
      inspirationImageIds: [...params.inspirationImageIds],
      linkedJerseyIds: [...(params.linkedJerseyIds || [])],
      pricing: {
        priceUsd: params.pricing.priceUsd,
        priceGbp: params.pricing.priceGbp,
        priceEur: params.pricing.priceEur,
      },
      appStoreProductId: params.appStoreProductId.trim(),
      playStoreProductId: params.playStoreProductId.trim(),
      stripeProductId: params.stripeProductId.trim(),
      isActive: params.isActive,
      startAtMs:
        startAtMs !== null && Number.isFinite(startAtMs) ? startAtMs : null,
      endAtMs: endAtMs !== null && Number.isFinite(endAtMs) ? endAtMs : null,
      startAtLocal: params.startAtLocal || null,
      endAtLocal: params.endAtLocal || null,
      sortOrder: params.sortOrder,
      updatedAt: now,
      updatedBy: adminId,
    });

    if (oldStoragePath) {
      try {
        await deleteObject(storageRef(storage, oldStoragePath));
      } catch (err) {
        console.warn(
          "[AdminService] Failed to delete old event template image:",
          err
        );
      }
    }

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "update_event_template",
      targetType: "media",
      targetId: id,
      details: {
        startAtMs,
        endAtMs,
        isActive: params.isActive,
        inspirationCount: params.inspirationImageIds.length,
      },
    });
  }

  async deleteEventTemplate(id: string, adminId: string): Promise<void> {
    const path = `${this.eventTemplatesPath()}/${id}`;
    const snapshot = await get(ref(db, path));
    if (!snapshot.exists()) return;

    const event = snapshot.val() as EventTemplate;
    if (event.imageStoragePath) {
      try {
        await deleteObject(storageRef(storage, event.imageStoragePath));
      } catch (err) {
        console.warn(
          "[AdminService] Failed to delete event template image:",
          err
        );
      }
    }
    await set(ref(db, path), null);

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "delete_event_template",
      targetType: "media",
      targetId: id,
      details: {},
    });
  }

  /**
   * Build the `notification` subdocument that should be written to RTDB,
   * accounting for:
   *   - the toggle (null/undefined `next` means "no push for this campaign")
   *   - a new image upload (replaces existing imageUrl/path)
   *   - preserving server-managed fields from the existing record
   *     (`sentAtMs`, `lastSendReport`) so the form doesn't clobber them.
   */
  private async resolveNotificationForWrite(
    campaignId: string,
    next: import("../types").CampaignNotification | undefined,
    imageFile: File | undefined,
    existing: import("../types").CampaignNotification | undefined
  ): Promise<import("../types").CampaignNotification | undefined> {
    if (!next) return undefined;

    const en = next.translations?.en;
    if (!en?.title?.trim() || !en?.body?.trim()) {
      throw new Error(
        "Notification requires English title and body when push is enabled."
      );
    }
    if (!Number.isFinite(next.sendAtMs)) {
      throw new Error("Notification send-at datetime is invalid.");
    }

    let imageUrl = next.imageUrl;
    let imageStoragePath = next.imageStoragePath;

    if (imageFile) {
      const uploaded = await this.uploadCampaignNotificationImage(
        campaignId,
        imageFile
      );
      imageUrl = uploaded.imageUrl;
      imageStoragePath = uploaded.imageStoragePath;
    }

    const sanitized: import("../types").CampaignNotification = {
      translations: sanitizeNotificationTranslations(next.translations),
      sendAtMs: next.sendAtMs,
      sendAtLocal: next.sendAtLocal,
      deeplinkUrl: (next.deeplinkUrl || "").trim(),
      ...(imageUrl ? { imageUrl } : {}),
      ...(imageStoragePath ? { imageStoragePath } : {}),
      // Preserve server-managed fields if they already exist on the record.
      ...(existing?.sentAtMs ? { sentAtMs: existing.sentAtMs } : {}),
      ...(existing?.lastSendReport
        ? { lastSendReport: existing.lastSendReport }
        : {}),
    };

    return sanitized;
  }

  /**
   * Trigger an FCM dispatch for a campaign's notification. Server enforces
   * admin gating and writes back `sentAtMs` / `lastSendReport` for `'all'`
   * sends. Test sends (`'me'` / `'testUsers'`) only update `lastSendReport`.
   */
  async sendCampaignNotification(
    campaignId: string,
    scope: "me" | "testUsers" | "all"
  ): Promise<{ delivered: number; failed: number }> {
    const fn = httpsCallable<
      { campaignId: string; scope: "me" | "testUsers" | "all" },
      { delivered: number; failed: number }
    >(functions, "sendCampaignNotificationCallable");
    try {
      const result = await fn({ campaignId, scope });
      return result.data;
    } catch (err) {
      throw new Error(getErrorMessage(err));
    }
  }

  async deleteCampaign(id: string, adminId: string): Promise<void> {
    const path = `${this.campaignsPath()}/${id}`;
    const snapshot = await get(ref(db, path));
    if (!snapshot.exists()) return;

    const campaign = snapshot.val() as Campaign;
    if (campaign.imageStoragePath) {
      try {
        await deleteObject(storageRef(storage, campaign.imageStoragePath));
      } catch (err) {
        console.warn("[AdminService] Failed to delete campaign image:", err);
      }
    }
    if (campaign.notification?.imageStoragePath) {
      try {
        await deleteObject(
          storageRef(storage, campaign.notification.imageStoragePath)
        );
      } catch (err) {
        console.warn(
          "[AdminService] Failed to delete campaign notification image:",
          err
        );
      }
    }
    await set(ref(db, path), null);

    await this.logAdminAction({
      adminId,
      adminEmail: "",
      action: "delete_campaign",
      targetType: "campaign",
      targetId: id,
      details: {},
    });
  }
}

/**
 * Strips empty notification translations (other than English) before write,
 * so the RTDB record stays clean when an admin clears a language's strings.
 */
function sanitizeNotificationTranslations(
  translations: import("../types").CampaignNotification["translations"]
): import("../types").CampaignNotification["translations"] {
  const out: import("../types").CampaignNotification["translations"] = {
    en: {
      title: translations.en?.title?.trim() || "",
      body: translations.en?.body?.trim() || "",
    },
  };
  for (const [lang, t] of Object.entries(translations)) {
    if (lang === "en") continue;
    const title = t?.title?.trim() || "";
    const body = t?.body?.trim() || "";
    if (!title && !body) continue;
    out[lang] = { title, body };
  }
  return out;
}

/** Strips empty translation entries (other than English) before write. */
function sanitizeTranslations(
  translations: Campaign["translations"]
): Campaign["translations"] {
  const out: Campaign["translations"] = {
    en: {
      title: translations.en?.title?.trim() || "",
      description: translations.en?.description?.trim() || "",
      buttonLabel: translations.en?.buttonLabel?.trim() || "",
    },
  };
  for (const [lang, t] of Object.entries(translations)) {
    if (lang === "en") continue;
    const title = t?.title?.trim() || "";
    const description = t?.description?.trim() || "";
    const buttonLabel = t?.buttonLabel?.trim() || "";
    // Drop the language entirely if it's all blank - RTDB treats undefined
    // values as deletion, which keeps the record clean.
    if (!title && !description && !buttonLabel) continue;
    out[lang] = { title, description, buttonLabel };
  }
  return out;
}

/**
 * Validate event-template form params before write. Throws on the first
 * problem so the admin sees a single, actionable error message. Mirrors the
 * UI-side validations in AdminEventTemplatesPage but is the source of truth
 * for what actually hits RTDB.
 */
function validateEventTemplateForm(
  params: EventTemplateFormParams,
  requireImage: boolean
): void {
  if (requireImage && !params.imageFile) {
    throw new Error("A cover image is required.");
  }
  const en = params.translations?.en;
  if (!en?.title?.trim()) {
    throw new Error("English title is required.");
  }
  if (!en?.description?.trim()) {
    throw new Error("English description is required.");
  }
  if (!en?.ctaLabel?.trim()) {
    throw new Error("English CTA label is required.");
  }
  if (
    !Array.isArray(params.inspirationImageIds) ||
    params.inspirationImageIds.length === 0
  ) {
    throw new Error("At least one inspiration image is required.");
  }
  if (params.inspirationImageIds.length > 50) {
    throw new Error("A maximum of 50 inspirations can be attached.");
  }
  if (
    !Array.isArray(params.linkedJerseyIds) ||
    params.linkedJerseyIds.length === 0
  ) {
    throw new Error("At least one linked jersey is required.");
  }
  if (!params.pricing) {
    throw new Error("Pricing is required.");
  }
  if (!(params.pricing.priceUsd > 0)) {
    throw new Error("USD price must be greater than zero.");
  }
  if (!(params.pricing.priceGbp > 0)) {
    throw new Error("GBP price must be greater than zero.");
  }
  if (!(params.pricing.priceEur > 0)) {
    throw new Error("EUR price must be greater than zero.");
  }
  if (!params.appStoreProductId?.trim()) {
    throw new Error("App Store product ID is required.");
  }
  if (!params.playStoreProductId?.trim()) {
    throw new Error("Play Store product ID is required.");
  }
  // Stripe product ID is optional - web checkout may not be wired yet.
}

/** Strips empty translation entries (other than English) before write. */
function sanitizeEventTranslations(
  translations: EventTemplate["translations"]
): EventTemplate["translations"] {
  const out: EventTemplate["translations"] = {
    en: {
      title: translations.en?.title?.trim() || "",
      description: translations.en?.description?.trim() || "",
      ctaLabel: translations.en?.ctaLabel?.trim() || "",
    },
  };
  for (const [lang, t] of Object.entries(translations)) {
    if (lang === "en") continue;
    const title = t?.title?.trim() || "";
    const description = t?.description?.trim() || "";
    const ctaLabel = t?.ctaLabel?.trim() || "";
    if (!title && !description && !ctaLabel) continue;
    out[lang] = { title, description, ctaLabel };
  }
  return out;
}

/**
 * Permissive URL validator: accepts http(s) URLs with a host, or
 * `fourkmatch://...` deep links to internal app pages (e.g.
 * `fourkmatch://home`, `fourkmatch://event/abc123`).
 */
function isValidUrl(value: string): boolean {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed.startsWith("fourkmatch://")) {
    return trimmed.length > "fourkmatch://".length;
  }
  try {
    const u = new URL(trimmed);
    return (u.protocol === "http:" || u.protocol === "https:") && !!u.host;
  } catch {
    return false;
  }
}

export const adminService = new AdminService();
export default adminService;
